import { type ChangeEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { adjustBatch, listBatches } from '../../api/stock'
import { getMyPractices } from '../../api/staff'
import { ButtonLink, Button } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { selectClass, textareaClass } from '../../components/ui/FormField'
import { NumberStepper } from '../../components/ui/NumberStepper'
import { useToast } from '../../components/ui/Toast'
import { useAuth } from '../../context/AuthContext'
import { useFetch } from '../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass } from '@/lib/tableStyles'
import { formatCurrency } from '@/lib/currency'
import type { StockBatchListItem } from '../../types/stock'

interface AdjustState {
  batchId: number
  quantity: string
  notes: string
  submitting: boolean
  error: string | null
}

export default function StockList() {
  const toast = useToast()
  const { user } = useAuth()
  const canAdjustStock = user?.role !== 'org_admin'

  const [selectedPracticeId, setSelectedPracticeId] = useState<number | null>(null)
  const [adjust, setAdjust] = useState<AdjustState | null>(null)

  const { data: practices, loading: practicesLoading, error: practicesError } = useFetch(
    () => getMyPractices(),
    [],
  )

  const practiceId = selectedPracticeId ?? practices?.[0]?.id ?? null

  const { data: batches, loading, error, refetch } = useFetch(
    () => {
      if (!practiceId) return Promise.resolve([] as StockBatchListItem[])
      return listBatches({ practice_id: practiceId })
    },
    [practiceId],
  )

  const grouped = batches
    ? batches.reduce<Map<number, { product_name: string; batches: StockBatchListItem[] }>>((map, b) => {
        if (!map.has(b.product_id)) map.set(b.product_id, { product_name: b.product_name, batches: [] })
        map.get(b.product_id)!.batches.push(b)
        return map
      }, new Map())
    : new Map()

  const openAdjust = (batchId: number, currentQty: string) => {
    setAdjust({ batchId, quantity: currentQty, notes: '', submitting: false, error: null })
  }

  const closeAdjust = () => setAdjust(null)

  const submitAdjust = async () => {
    if (!adjust) return
    const qty = adjust.quantity.trim()
    if (!qty || isNaN(Number(qty))) {
      setAdjust((a) => a ? { ...a, error: 'Enter a valid quantity.' } : null)
      return
    }
    if (Number(qty) < 0) {
      setAdjust((a) => a ? { ...a, error: 'Quantity cannot be negative.' } : null)
      return
    }
    setAdjust((a) => a ? { ...a, submitting: true, error: null } : null)
    try {
      await adjustBatch(adjust.batchId, { quantity: qty, notes: adjust.notes.trim() || null })
      toast.success('Stock adjusted.')
      setAdjust(null)
      refetch()
    } catch (err) {
      setAdjust((a) => a ? { ...a, submitting: false, error: err instanceof Error ? err.message : 'Adjustment failed.' } : null)
    }
  }

  if (practicesLoading) return <div className="text-text-2 p-8">Loading…</div>
  if (practicesError) return <div className="p-8"><ErrorBox>{practicesError}</ErrorBox></div>
  if (!practices || practices.length === 0) {
    return <div className="p-8"><ErrorBox>No practices found.</ErrorBox></div>
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">Stock</h1>
        <div className="flex gap-3 items-center flex-wrap">
          {practices.length > 1 && (
            <select
              className={`${selectClass()} w-[200px]`}
              value={practiceId ?? ''}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedPracticeId(Number(e.target.value))}
            >
              {practices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <ButtonLink to="/stock/batches/new" size="md">+ Receive Stock</ButtonLink>
        </div>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {loading ? (
        <Card><div className="text-text-3 py-6">Loading stock…</div></Card>
      ) : grouped.size === 0 ? (
        <Card>
          <div className="text-text-3 text-center p-8">
            No stock found.{' '}
            <Link to="/stock/products" className="text-accent font-semibold no-underline hover:underline">Add products to your catalog</Link>
            {' '}first, then receive stock.
          </div>
        </Card>
      ) : (
        Array.from(grouped.entries()).map(([productId, group]) => {
          const total = group.batches.reduce((sum: number, b: StockBatchListItem) => sum + parseFloat(b.quantity_on_hand ?? '0'), 0)
          return (
            <Card key={productId} className="mb-5">
              <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                <h3 className="m-0 text-base font-bold text-text-heading">
                  {group.product_name}
                </h3>
                <span className="text-sm text-text-3">
                  Total on hand: <strong className="text-text">{total.toLocaleString()} {group.batches[0]?.product_unit}</strong>
                </span>
              </div>

              <SectionTitle className="mb-2">Batches</SectionTitle>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className={thClass}>Batch #</th>
                      <th className={thClass}>Expiry</th>
                      <th className={thClass}>Qty</th>
                      <th className={thClass}>Cost price</th>
                      <th className={thClass}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={tbodyHoverClass}>
                    {group.batches.map((b: StockBatchListItem) => {
                      const isAdjusting = adjust !== null && adjust.batchId === b.id
                      const activeAdjust = isAdjusting ? adjust : null
                      return (
                        <>
                          <tr key={b.id}>
                            <td className={`${tdClass} font-mono text-[0.82rem]`}>
                              {b.batch_number ?? <span className="text-text-3">—</span>}
                            </td>
                            <td className={tdClass}>
                              {b.expiry_date ? (
                                <span>
                                  {b.expiry_date}
                                  {b.is_expired && (
                                    <span className="ml-[0.4rem] bg-danger-bg text-danger-text text-[0.72rem] font-semibold py-[0.1rem] px-[0.45rem] rounded-full">
                                      Expired
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-text-3">—</span>
                              )}
                            </td>
                            <td className={`${tdClass} font-semibold`}>{b.quantity_on_hand}</td>
                            <td className={tdClass}>{b.cost_price ? formatCurrency(b.cost_price) : <span className="text-text-3">—</span>}</td>
                            <td className={tdClass}>
                              {canAdjustStock && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => isAdjusting ? closeAdjust() : openAdjust(b.id, b.quantity_on_hand)}
                                >
                                  {isAdjusting ? 'Cancel' : 'Adjust'}
                                </Button>
                              )}
                            </td>
                          </tr>
                          {activeAdjust !== null && (
                            <tr key={`${b.id}-adjust`}>
                              <td colSpan={5} className="py-3 px-[0.7rem] bg-surface-alt border-b border-border">
                                <div className="flex gap-3 flex-wrap items-start">
                                  <div>
                                    <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">
                                      New quantity <span className="text-danger-text">*</span>
                                      <span className="font-normal text-text-3 ml-[0.4rem]">(on hand: {b.quantity_on_hand})</span>
                                    </label>
                                    <NumberStepper
                                      min={0}
                                      step={0.01}
                                      className="w-[150px]"
                                      hasError={!!activeAdjust.error}
                                      value={activeAdjust.quantity}
                                      onChange={(v) => setAdjust((a) => a ? { ...a, quantity: v, error: null } : null)}
                                      disabled={activeAdjust.submitting}
                                      placeholder="0"
                                      autoFocus
                                    />
                                    {activeAdjust.error && (
                                      <span className="text-danger-text text-[0.78rem] block mt-[0.2rem]">{activeAdjust.error}</span>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-[200px]">
                                    <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">Notes</label>
                                    <textarea
                                      className={`${textareaClass()} min-h-[60px]`}
                                      value={activeAdjust.notes}
                                      onChange={(e) => setAdjust((a) => a ? { ...a, notes: e.target.value } : null)}
                                      disabled={activeAdjust.submitting}
                                      placeholder="Reason for adjustment…"
                                    />
                                  </div>
                                  <div className="flex gap-2 items-end pb-[0.1rem]">
                                    <Button
                                      size="sm"
                                      disabled={activeAdjust.submitting}
                                      onClick={() => void submitAdjust()}
                                    >
                                      {activeAdjust.submitting ? 'Saving…' : 'Save'}
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={closeAdjust} disabled={activeAdjust.submitting}>
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )
        })
      )}
    </div>
  )
}
